import { ComponentProps, ReactNode, Ref, useRef } from 'react';
import { AriaButtonProps, mergeProps, useButton, useFocusRing } from 'react-aria';

import composeRefs from '@seznam/compose-react-refs';

import Badge from '~/components/scales/Badge/Badge';
import { TooltipTriggerProps, pickTooltipTriggerProps } from '~/components/scales/Tooltip';
import { validateDataCy } from '~/components/scales/utils/data-cy';

type Props = TooltipTriggerProps & {
  autoFocus?: boolean;
  badge?: Omit<ComponentProps<typeof Badge>, 'className'>;
  /**
   * @deprecated Avoid relying on data-cy for tests. Prefer accessible queries:
   * `screen.getByRole('button', { name: 'Label' })`
   */
  'data-cy'?: string;
  endIcon?: ReactNode;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isPressed?: boolean;
  label: string;
  onClick?: AriaButtonProps['onPress'];
  ref?: Ref<HTMLButtonElement>;
  startIcon?: ReactNode;
  type: 'primary' | 'secondary' | 'destructive' | 'warning' | 'toolbar';
};

export default function Button(props: Props) {
  const ref = useRef(null);

  // Manage focus via useFocusRing since focus has a habit of appearing when clicking via
  // the mouse when we rely solely on useButton.
  const { isFocusVisible, focusProps } = useFocusRing();

  const { buttonProps } = useButton(
    {
      'aria-pressed': props.isPressed,
      autoFocus: props.autoFocus,
      isDisabled: Boolean(props.isDisabled),
      onPress: props.onClick,
    },
    ref
  );

  const classes = [
    'group flex relative shrink-0 grow-0 type-button justify-center items-center gap-1 border-2 h-10 transition',
  ];

  if (props.isFullWidth) {
    classes.push('w-full');
  } else {
    classes.push('w-min whitespace-nowrap');
  }

  let roundedClass = 'rounded-3xl';

  if (props.type === 'primary') {
    classes.push(
      props.isPressed ? 'bg-button-primary-pressed' : 'bg-button-primary',
      'text-type-secondary border-transparent',
      'hover:enabled:bg-button-primary-hover',
      isFocusVisible ? 'outline border-button-focus-outline' : 'outline-none',
      'disabled:text-type-inactive disabled:bg-button-inactive disabled:border-button-inactive'
    );
  } else if (props.type === 'secondary') {
    classes.push(
      props.isPressed ? 'bg-button-secondary-pressed' : 'bg-button-secondary',
      'text-type-primary border-button-primary',
      'hover:enabled:bg-button-secondary-hover',
      isFocusVisible ? 'outline !border-transparent' : 'outline-none',
      'disabled:text-type-inactive disabled:border-button-inactive'
    );
  } else if (props.type === 'destructive') {
    classes.push(
      props.isPressed ? 'bg-button-destructive-pressed' : 'bg-button-secondary',
      'text-button-destructive border-button-destructive',
      'hover:enabled:bg-button-destructive-hover',
      isFocusVisible
        ? 'outline outline-[3px] outline-button-destructive !border-transparent'
        : 'outline-none',
      'disabled:text-type-inactive disabled:border-button-inactive'
    );
  } else if (props.type === 'warning') {
    classes.push(
      props.isPressed ? 'bg-button-warning-pressed' : 'bg-button-warning',
      'text-button-warning-text border-transparent',
      'hover:enabled:bg-button-warning-hover',
      isFocusVisible
        ? 'outline outline-[3px] outline-button-warning-focus-outline'
        : 'outline-none',
      'disabled:text-type-inactive disabled:bg-button-inactive'
    );
  } else if (props.type === 'toolbar') {
    classes.push(
      props.isPressed ? 'bg-button-toolbar-pressed' : 'bg-button-toolbar',
      'type-body2 !font-bold border-transparent',
      'hover:enabled:bg-button-toolbar-hover',
      isFocusVisible ? 'outline !border-transparent' : 'outline-none',
      'disabled:text-type-inactive'
    );
    roundedClass = 'rounded';
  }

  let paddingClass = 'px-4';
  if (props.startIcon && props.endIcon) {
    paddingClass = 'px-2';
  } else if (props.startIcon) {
    paddingClass = 'pl-2 pr-4';
  } else if (props.endIcon) {
    paddingClass = 'pl-4 pr-2';
  }

  validateDataCy(props['data-cy'], 'button');

  return (
    <button
      {...mergeProps(pickTooltipTriggerProps(props), buttonProps, focusProps)}
      ref={composeRefs(ref, props.ref)}
      className={[...classes, paddingClass, roundedClass].join(' ')}
      data-cy={props['data-cy']}
    >
      {props.startIcon}
      <span>{props.label}</span>
      {props.endIcon}
      {props.badge && <Badge {...props.badge} className="absolute -right-2 -top-2" />}
    </button>
  );
}
